import { useState } from 'react';
import { Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal, Eyebrow } from './Reveal';
import { API } from '../App';

export default function Estimator() {
  const [description, setDescription] = useState('');
  const [result, setResult] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10 || busy) return;
    setResult('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/ai/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Estimator unavailable — try again.');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) setResult((r) => r + parsed.delta);
          } catch { /* partial */ }
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="estimator" data-testid="ai-estimator-section" className="py-24 sm:py-32 bg-[#0B1528]/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal className="text-center">
          <Eyebrow>AI Project Estimator</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Describe your project. <span className="text-gradient-gold">Get a scoped estimate in seconds.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-base">Powered by Gemini — a draft scope, the right package and a price range, instantly.</p>
        </Reveal>

        <Reveal i={1}>
          <form onSubmit={run} className="mt-12 gold-ring rounded-3xl bg-[#0F1C33]/70 p-8">
            <textarea
              data-testid="estimator-input"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. I run a two-truck plumbing company in Athens GA. I need a site where homeowners can get an instant quote for common jobs and book a visit without calling me…"
              className="w-full bg-[#050B14]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-5 py-4 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600"
            />
            <button
              data-testid="estimator-run-button"
              type="submit"
              disabled={busy || description.trim().length < 10}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-shadow duration-300 disabled:opacity-50"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
              Generate my estimate
            </button>

            {(result || busy) && (
              <div data-testid="estimator-result" className="mt-6 border border-amber-500/25 bg-amber-500/5 rounded-2xl p-6">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] text-amber-300/90 uppercase mb-3">Draft scope</div>
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{result || 'Thinking…'}</p>
                {!busy && result && (
                  <a href="/book" data-testid="estimator-book-link"
                    className="mt-5 inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200 transition-colors">
                    Book the free consultation to lock this in <ArrowRight size={15} />
                  </a>
                )}
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
